/* eslint-disable no-console */
// src/heic.js — faster HEIC preview path (shorter Chrome timeout, smaller default preview)
// API COMPATIBLE with your previous heic.js

import heic2any from 'heic2any';
import exifr from 'exifr';

// ---------- Detection ----------
export const isHeicByNameOrMime = (file) => {
  const name = (file?.name || '').toLowerCase();
  const type = (file?.type || '').toLowerCase();
  return (
    type === 'image/heic' ||
    type === 'image/heif' ||
    name.endsWith('.heic') ||
    name.endsWith('.heif')
  );
};

async function isHeicByMagic(file) {
  try {
    const buf = await readBytes(file, 12);
    if (buf.byteLength < 12) return false;
    const view = new DataView(buf);
    const boxType = ascii(view, 4, 4); // 'ftyp'
    if (boxType !== 'ftyp') return false;
    const major = ascii(view, 8, 4);
    const brands = ['heic', 'heix', 'hevc', 'hevx', 'heis', 'hevm'];
    return brands.includes(major);
  } catch { return false; }
}

export async function isHeicFile(file) {
  if (isHeicByNameOrMime(file)) return true;
  return await isHeicByMagic(file);
}

// ---------- Previews ----------
export async function getPreviewDataUrl(file, { maxW = 1200, quality = 0.82 } = {}) {
  const isHeic = await isHeicFile(file);

  // Non-HEIC → simple downscale
  if (!isHeic) {
    const out = await readAndDownscaleToDataUrl(file, { maxW, quality });
    return out;
  }

  // (a) Safari/iOS native decode (fast timeout on Chrome to avoid 2s stalls)
  try {
    const url = URL.createObjectURL(file);
    await ensureImageLoads(url, 450); // ↓ from 2000 → ~0.45s to avoid slow fail path on Chrome
    const dataUrl = await drawToCanvasDataUrl(url, { maxW, quality });
    URL.revokeObjectURL(url);
    return dataUrl;
  } catch {
    // expected on Chrome/most desktops
  }

  // (b) heic2any conversion (then we may still downscale to maxW)
  try {
    const out = await heic2any({ blob: file, toType: 'image/jpeg', quality });
    const blob = Array.isArray(out) ? out[0] : out;
    // If the converted JPEG is still large, downscale to maxW for a smaller preview
    const dataUrl = await blobToDataUrl(blob);
    const resized = await drawToCanvasDataUrl(dataUrl, { maxW, quality });
    await ensureImageLoads(resized, 1000);
    return resized;
  } catch (e) {
    console.warn('[heic] heic2any failed:', e?.message || e);
  }

  // (c) libheif-js (bundled) — only if available
  try {
    let HeifDecoder;
    try {
      ({ HeifDecoder } = await import('libheif-js/libheif-wasm/libheif-bundle.mjs'));
    } catch (e1) {
      ({ HeifDecoder } = await import('libheif-js/dist/libheif-bundle.mjs'));
    }
    if (HeifDecoder) {
      const dec = new HeifDecoder();
      const buf = new Uint8Array(await file.arrayBuffer());
      const images = dec.decode(buf);
      if (images && images.length) {
        const img = images[0];
        const w = img.get_width(), h = img.get_height();
        const rgba = new Uint8ClampedArray(w * h * 4);
        img.display(rgba, { premultiplied: true });
        // render to canvas, then scale down to maxW
        const big = document.createElement('canvas');
        big.width = w; big.height = h;
        big.getContext('2d').putImageData(new ImageData(rgba, w, h), 0, 0);
        const dataUrl = big.toDataURL('image/jpeg', quality);
        const resized = await drawToCanvasDataUrl(dataUrl, { maxW, quality });
        await ensureImageLoads(resized, 1000);
        return resized;
      }
    }
  } catch (e) {
    console.warn('[heic] libheif-bundle fallback failed:', e?.message || e);
  }

  // (d) Cloud fallback (Cloudinary) — requires REACT_APP_ env vars
  try {
    const cloud = await convertHeicViaCloudinary(file, quality);
    if (cloud) {
      await ensureImageLoads(cloud, 1500);
      return cloud;
    }
  } catch (e) {
    console.warn('[heic] cloud fallback failed:', e?.message || e);
  }

  // (e) Last resort — object URL (may be broken in Chrome)
  console.warn('[heic] returning object URL fallback');
  return URL.createObjectURL(file);
}

// ---------- EXIF ----------
export async function extractExifLite(file) {
  try {
    const meta = await exifr.parse(file, { gps: true, tiff: true, ifd0: true, exif: true, translateValues: false });
    if (!meta || typeof meta !== 'object') return fallbackMeta(file);

    const lat = meta.latitude ?? meta.GPSLatitude;
    const lng = meta.longitude ?? meta.GPSLongitude;
    const hasGPS = typeof lat === 'number' && typeof lng === 'number';

    const rawDate = meta.DateTimeOriginal || meta.CreateDate || meta.ModifyDate || meta.dateTimeOriginal;
    const dateTaken = toDateISO(rawDate) || fromLastModified(file) || todayISO();

    return { hasGPS, coordinates: hasGPS ? { lat, lng } : null, dateTaken, fileName: file?.name || 'image', raw: meta };
  } catch {
    return fallbackMeta(file);
  }
}

// ---------- Main pipeline ----------
export async function buildPhotoObject(file) {
  const [isHeic, previewUrl, exif] = await Promise.all([
    isHeicFile(file),
    getPreviewDataUrl(file),   // now faster defaults and shorter Chrome timeout
    extractExifLite(file),
  ]);

  const type = (file?.type || '').toLowerCase();
  const name = (file?.name || '').toLowerCase();

  const sourceType = isHeic
    ? 'heic'
    : type.startsWith('image/')
    ? type.replace('image/', '')
    : name.endsWith('.jpg') || name.endsWith('.jpeg') ? 'jpeg'
    : name.endsWith('.png') ? 'png'
    : name.endsWith('.webp') ? 'webp'
    : 'unknown';

  return {
    url: previewUrl,
    originalFile: file,
    isHEIC: isHeic,
    sourceType,
    converted: isHeic,
    likes: 0,
    gpsExtracted: !!exif.hasGPS,
    fileName: file?.name || 'image',
    ...exif,
  };
}

// ---------- Cloud fallback ----------
async function convertHeicViaCloudinary(file, quality = 0.82) {
  const cloud = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  const preset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
  if (!cloud || !preset) {
    console.warn('[heic] Cloudinary env vars missing');
    return null;
  }

  const form = new FormData();
  form.append('file', file);
  form.append('upload_preset', preset);

  const uploadUrl = `https://api.cloudinary.com/v1_1/${cloud}/image/upload`;
  const res = await fetch(uploadUrl, { method: 'POST', body: form });
  const json = await res.json();
  if (!json || !json.public_id) {
    console.warn('[heic] Cloudinary upload failed:', json?.error || json);
    return null;
  }

  const q = Math.round(Math.max(0.5, Math.min(1, quality)) * 100);
  const jpegUrl = `https://res.cloudinary.com/${cloud}/image/upload/f_jpg,q_${q},w_1200/${json.public_id}.jpg`;
  const blob = await fetch(jpegUrl, { mode: 'cors' }).then(r => r.blob());
  return await blobToDataUrl(blob);
}

// ---------- Internals ----------
function todayISO(){ return new Date().toISOString().slice(0,10); }
function fromLastModified(file){ return file?.lastModified ? new Date(file.lastModified).toISOString().slice(0,10) : null; }
function toDateISO(any){
  try {
    if (!any) return null;
    if (any instanceof Date) return any.toISOString().slice(0,10);
    if (typeof any === 'string') return new Date(any.replace(/:/g,'-').replace(' ','T')).toISOString().slice(0,10);
    return null;
  } catch { return null; }
}
async function readBytes(file,len){ const blob=file.slice(0,len); return await blob.arrayBuffer(); }
function ascii(view,off,len){ let s=''; for(let i=0;i<len;i++) s+=String.fromCharCode(view.getUint8(off+i)); return s; }
async function ensureImageLoads(url, timeoutMs = 800) {
  await new Promise((resolve, reject) => {
    const img = new Image(); let done = false;
    const t = setTimeout(() => { if (!done) { done = true; reject(new Error('timeout')); } }, Math.max(350, timeoutMs));
    img.onload = () => { if (!done) { done = true; clearTimeout(t); resolve(); } };
    img.onerror = (e) => { if (!done) { done = true; clearTimeout(t); reject(e); } };
    img.src = url;
  });
}
async function readAndDownscaleToDataUrl(file,{maxW=1200,quality=0.82} = {}){
  const dataUrl = await blobToDataUrl(file);
  return await drawToCanvasDataUrl(dataUrl, { maxW, quality });
}
async function drawToCanvasDataUrl(srcUrl,{maxW=1200,quality=0.82} = {}){
  const bmp = await loadBitmap(srcUrl);
  const scale = bmp.width > bmp.height ? Math.min(1, maxW / bmp.width) : Math.min(1, maxW / bmp.height);
  const w = Math.max(1, Math.round(bmp.width * scale));
  const h = Math.max(1, Math.round(bmp.height * scale));

  // Use OffscreenCanvas/CreateImageBitmap when available (faster in Chrome)
  const CanvasCtor = (typeof OffscreenCanvas !== 'undefined') ? OffscreenCanvas : null;
  if (CanvasCtor) {
    const oc = new CanvasCtor(w, h);
    const ctx = oc.getContext('2d');
    ctx.drawImage(bmp, 0, 0, w, h);
    const blob = await oc.convertToBlob({ type: 'image/jpeg', quality });
    return await blobToDataUrl(blob);
  } else {
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bmp, 0, 0, w, h);
    return canvas.toDataURL('image/jpeg', quality);
  }
}
async function loadBitmap(url){
  if ('createImageBitmap' in window) {
    const resp = await fetch(url);
    const blob = await resp.blob();
    return await createImageBitmap(blob);
  }
  return await loadImage(url);
}
function loadImage(url){ return new Promise((resolve,reject)=>{ const img=new Image(); img.onload=()=>resolve(img); img.onerror=reject; img.src=url; }); }
function blobToDataUrl(blob){ return new Promise((resolve,reject)=>{ const r=new FileReader(); r.onload=()=>resolve(r.result); r.onerror=reject; r.readAsDataURL(blob); }); }
function fallbackMeta(file){ return { hasGPS:false, coordinates:null, dateTaken:fromLastModified(file)||todayISO(), fileName:file?.name||'image', raw:null }; }
